import { participantChannels, StageRole, TrackerChannel, TrackerConfig } from './tracker-config';
import { TrackerCloseAs, TrackerRollup, TrackerTouchKind } from './tracker-rollup';
import { MESSAGE_LABEL } from './tracker-copy';

export const MAX_PEOPLE_PER_PASTE = 500;

export const TRACKER_OFF_MESSAGE =
  'The tracker is switched off for this course. Ask your coach if you need it back.';

export type TrackerPersonFields = {
  firstName: string;
  fullName: string;
  offer: string;
  email: string;
  phone: string;
  segment: string;
  tier: string;
  note: string;
};

/** One offer aimed at one audience, the participant's own: "Coaches · 90-day coaching package". */
export type TrackerBet = {
  id: string;
  name: string;
  offer: string;
  position: number;
};

/** Short enough for the bet switch and a row's second line. */
export const MAX_BET_NAME_LENGTH = 30;
export const MAX_BET_OFFER_LENGTH = 120;

export const PICK_A_BET_MESSAGE = 'Pick a bet for this person.';
export const PICK_A_BET_FOR_LIST_MESSAGE = 'Pick a bet for this list.';
export const ADD_BETS_FIRST_MESSAGE = 'Add your bets first. Every person goes under one bet.';

export type TrackerPerson = TrackerPersonFields & {
  id: string;
  channelKey: string;
  /** Set on a course with bets on: every person goes under one. */
  betId?: string;
  stageKey?: string;
  nextStep: string;
  nextStepKey?: string;
  /** YYYY-MM-DD. */
  nextOn?: string;
  createdAt: Date | string;
};

export type TrackerTouch = {
  id: string;
  personId: string;
  kind: TrackerTouchKind;
  stageKey?: string;
  stepKey?: string;
  label: string;
  /** YYYY-MM-DD. */
  happenedOn: string;
  /** YYYY-MM-DD, the conversation or decision date. */
  forOn?: string;
  amountCents?: number;
  createdAt: Date | string;
};

export const FIELD_LIMITS: Record<keyof TrackerPersonFields, number> = {
  firstName: 80,
  fullName: 160,
  offer: 120,
  email: 254,
  phone: 40,
  segment: 80,
  tier: 80,
  note: 2000,
};

export const MAX_NEXT_STEP_LENGTH = 60;
export const MAX_TOUCH_LABEL_LENGTH = 60;

export type TrackerGroup =
  | 'overdue'
  | 'due_today'
  | 'coming_up'
  | 'decision'
  | 'won_payment'
  | 'in_progress'
  | 'done';

export const TRACKER_GROUP_ORDER: TrackerGroup[] = [
  'overdue',
  'due_today',
  'coming_up',
  'in_progress',
  'decision',
  'won_payment',
  'done',
];

export const TRACKER_GROUP_TITLES: Record<TrackerGroup, string> = {
  overdue: 'Overdue',
  due_today: 'Due today',
  coming_up: 'Coming up',
  in_progress: 'In progress',
  decision: 'Decision pending',
  won_payment: 'Won, payment to log',
  done: 'Done',
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function utcDate(dateIso: string) {
  return new Date(`${dateIso}T00:00:00Z`);
}

/** "Thu, Nov 19", the portal's date style, with the year only when it isn't today's year. */
export function fmtTrackerDate(dateIso: string, today: string) {
  const date = utcDate(dateIso);
  const base = `${WEEKDAYS[date.getUTCDay()]}, ${fmtShortDate(dateIso)}`;
  return dateIso.slice(0, 4) === today.slice(0, 4) ? base : `${base}, ${date.getUTCFullYear()}`;
}

/** "Nov 8". */
export function fmtShortDate(dateIso: string) {
  const date = utcDate(dateIso);
  return `${MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

export function daysBetween(fromIso: string, toIso: string) {
  return Math.round((utcDate(toIso).getTime() - utcDate(fromIso).getTime()) / 86400000);
}

export function isIsoDate(text: string) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return false;
  }
  const date = utcDate(text);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === text;
}

/**
 * Cents from "$4,800", "4800.50" or "4 800". Undefined when it isn't a
 * plain amount of money.
 */
export function parseMoneyCents(text: string): number | undefined {
  const cleaned = text.trim().replace(/^\$/, '').replace(/[,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) {
    return;
  }
  const [whole, fraction = ''] = cleaned.split('.');
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
}

/** "$4,800" or "$4,800.50". */
export function fmtMoney(cents: number) {
  const dollars = Math.floor(Math.abs(cents) / 100);
  const rest = Math.abs(cents) % 100;
  const whole = dollars.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${cents < 0 ? '-' : ''}$${whole}${rest ? `.${String(rest).padStart(2, '0')}` : ''}`;
}

/** "4800" or "4800.50", for an input's starting value. */
export function moneyInputValue(cents?: number) {
  if (cents === undefined) {
    return '';
  }
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
}

export function emptyPersonFields(): TrackerPersonFields {
  return {
    firstName: '',
    fullName: '',
    offer: '',
    email: '',
    phone: '',
    segment: '',
    tier: '',
    note: '',
  };
}

export function personName(person: Pick<TrackerPersonFields, 'firstName' | 'fullName'>) {
  return person.fullName.trim() || person.firstName.trim();
}

/** Trimmed and lower-cased, for matching names, emails and offers. */
export function fold(text: string) {
  return text.trim().toLowerCase();
}

/**
 * The person already in the tracker that `candidate` repeats: the same bet
 * when the candidate has one (one row per person per bet), otherwise the
 * same offer; and the same full name or, when given, the same email.
 */
export function findDuplicatePerson<T extends TrackerPersonFields & { betId?: string }>(
  candidate: TrackerPersonFields & { betId?: string },
  people: T[],
): T | undefined {
  const name = fold(personName(candidate));
  const email = fold(candidate.email);
  const offer = fold(candidate.offer);
  const sameSlot = candidate.betId
    ? (p: T) => p.betId === candidate.betId
    : (p: T) => fold(p.offer) === offer;
  return people.find(
    (p) =>
      sameSlot(p) &&
      ((!!name && fold(personName(p)) === name) || (!!email && fold(p.email) === email)),
  );
}

export function betOf<B extends { id: string }>(bets: B[] | undefined, betId?: string) {
  return betId ? bets?.find((b) => b.id === betId) : undefined;
}

/** The group of people with no bet: added before bets were switched on. */
export const NO_BET_ID = 'no-bet';
export const NO_BET_LABEL = 'No bet';

/** A bet, or the "No bet" group, as every per-bet figure splits the tracker. */
export type TrackerBetGroup = Pick<TrackerBet, 'id' | 'name' | 'offer'>;

/**
 * The groups every per-bet figure runs on: the participant's bets in the
 * server's order (position, then created_at), then "No bet" when some people
 * have none, so the groups always add up to the total. Empty with bets off
 * or no bets yet.
 */
export function trackerBetGroups<B extends TrackerBetGroup>(
  config: TrackerConfig,
  bets: B[] | undefined,
  people: Array<{ betId?: string }>,
): TrackerBetGroup[] {
  const list = config.bets ? (bets ?? []) : [];
  if (!list.length) {
    return [];
  }
  if (!people.some((p) => !betOf(list, p.betId))) {
    return list;
  }
  return [...list, { id: NO_BET_ID, name: NO_BET_LABEL, offer: '' }];
}

/** Whether a person is in a group: under that bet, or in "No bet" with no bet of theirs. */
export function inBetGroup(
  bets: Array<{ id: string }> | undefined,
  person: { betId?: string },
  groupId: string,
) {
  return groupId === NO_BET_ID ? !betOf(bets, person.betId) : person.betId === groupId;
}

/** The touches of one group's people. */
export function touchesOfGroup<T extends { personId: string }>(
  bets: Array<{ id: string }> | undefined,
  people: Array<{ id: string; betId?: string }>,
  touches: T[],
  groupId: string,
): T[] {
  const ids = new Set(people.filter((p) => inBetGroup(bets, p, groupId)).map((p) => p.id));
  return touches.filter((t) => ids.has(t.personId));
}

/** "Coaches · 90-day coaching package", or the name alone with no offer. */
export function betLabel(bet: Pick<TrackerBet, 'name' | 'offer'>) {
  return [bet.name.trim(), bet.offer.trim()].filter(Boolean).join(' · ');
}

/**
 * What a person's row shows where the offer goes: their bet's name on a
 * course with bets on (left off with one bet, where it is noise), otherwise
 * their own offer. A person with no bet, added before bets were switched on,
 * shows their own offer.
 */
export function personOfferText(
  config: TrackerConfig,
  bets: TrackerBet[] | undefined,
  person: Pick<TrackerPerson, 'offer' | 'betId'>,
) {
  const bet = config.bets ? betOf(bets, person.betId) : undefined;
  if (!bet) {
    return person.offer;
  }
  return (bets?.length ?? 0) > 1 ? bet.name : '';
}

/** "12 people are under “Coaches”." */
export function betPeopleText(people: number, name: string) {
  return `${people === 1 ? '1 person is' : `${people} people are`} under “${name}”.`;
}

/** "12 people are under “Coaches”. Move them to another bet first." */
export function betInUseMessage(people: number, name: string) {
  return `${betPeopleText(people, name)} Move them to another bet first.`;
}

export const ADD_ANOTHER_BET_MESSAGE = 'Add another bet first, or rename this one.';

/** "14 people are under a bet. Bets stay on." */
export function betsInUseMessage(people: number) {
  return `${people === 1 ? '1 person is' : `${people} people are`} under a bet. Bets stay on.`;
}

/** A full name's first word, the name used in messages. */
export function firstNameOf(fullName: string) {
  return fullName.trim().split(/\s+/)[0] ?? '';
}

export function stageByKey(config: TrackerConfig, key?: string) {
  if (!key) {
    return;
  }
  for (const channel of config.channels) {
    const stage = [...channel.stages, ...channel.exits].find((s) => s.key === key);
    if (stage) {
      return { channel, stage, isExit: channel.exits.includes(stage) };
    }
  }
}

export function stageRole(config: TrackerConfig, key?: string): StageRole | undefined {
  return stageByKey(config, key)?.stage.role;
}

export function stageLabel(config: TrackerConfig, key?: string) {
  return stageByKey(config, key)?.stage.label ?? 'Not started';
}

/** The stage carrying a role in a channel, e.g. the sequence's booking stage. */
export function stageForRole(channel: TrackerChannel, role: StageRole) {
  return [...channel.stages, ...channel.exits].find((s) => s.role === role);
}

export function channelOf(config: TrackerConfig, key: string) {
  return config.channels.find((c) => c.key === key);
}

/** The name of a person's next step: the sequence step's current name when its key still resolves. */
export function nextStepName(
  config: TrackerConfig,
  person: Pick<TrackerPerson, 'nextStep' | 'nextStepKey'>,
) {
  const step = person.nextStepKey
    ? config.sequence.find((s) => s.key === person.nextStepKey)
    : undefined;
  return step?.name ?? person.nextStep;
}

function compareTouches(a: TrackerTouch, b: TrackerTouch) {
  if (a.happenedOn !== b.happenedOn) {
    return a.happenedOn < b.happenedOn ? -1 : 1;
  }
  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
}

/** Oldest first, by date then entry order. */
export function sortTouches(touches: TrackerTouch[]) {
  return [...touches].sort(compareTouches);
}

/** A person's latest step touch on the stage carrying `role`, by date then entry order. */
export function latestStepByRole(config: TrackerConfig, touches: TrackerTouch[], role: StageRole) {
  const steps = sortTouches(touches).filter(
    (t) => t.kind === 'step' && stageRole(config, t.stageKey) === role,
  );
  return steps[steps.length - 1];
}

export function decisionOn(config: TrackerConfig, touches: TrackerTouch[]) {
  return latestStepByRole(config, touches, 'decision')?.forOn;
}

function isLastStage(config: TrackerConfig, person: TrackerPerson) {
  const channel = channelOf(config, person.channelKey);
  const last = channel?.stages[channel.stages.length - 1];
  return !!last && last.key === person.stageKey;
}

/** "Details · Thu, Nov 19", "Details · today", "Details · 2 days late", "Decision on Fri, Nov 20". */
export type TrackerNextText = { text: string; isLate: boolean; isMuted: boolean };

function shown(text: string, opts: { isLate?: boolean; isMuted?: boolean } = {}): TrackerNextText {
  return { text, isLate: !!opts.isLate, isMuted: !!opts.isMuted };
}

/**
 * "Details · Thu, Nov 19", "Details · today", "Details · 2 days late",
 * "Decision on Fri, Nov 20", "Payment to log", or "No next step".
 */
export function nextText(
  config: TrackerConfig,
  person: TrackerPerson,
  touches: TrackerTouch[],
  today: string,
  closeAs?: TrackerCloseAs,
): TrackerNextText {
  const role = stageRole(config, person.stageKey);
  if (role === 'replied_no' || role === 'lost') {
    return shown('No more touches');
  }
  if (role === 'won') {
    const awaitingPayment = closeAs === 'collected' && !touches.some((t) => t.kind === 'payment');
    return shown(awaitingPayment ? 'Payment to log' : '');
  }
  if (isLastStage(config, person)) {
    return shown('');
  }
  const name = nextStepName(config, person) || 'Follow up';
  if (person.nextOn) {
    if (person.nextOn === today) {
      return shown(`${name} · today`);
    }
    if (person.nextOn < today) {
      const late = daysBetween(person.nextOn, today);
      return shown(`${name} · ${late === 1 ? '1 day' : `${late} days`} late`, { isLate: true });
    }
    return shown(`${name} · ${fmtTrackerDate(person.nextOn, today)}`);
  }
  if (role === 'decision') {
    const on = decisionOn(config, touches);
    return shown(on ? `Decision on ${fmtTrackerDate(on, today)}` : 'Decision pending');
  }
  const channel = channelOf(config, person.channelKey);
  if (channel?.flow === 'sequence' && (role === 'contacted' || !role)) {
    const sent = touches.some(
      (t) => t.kind === 'step' && stageRole(config, t.stageKey) === 'contacted',
    );
    if (sent) {
      return shown('Sequence finished');
    }
  }
  if (person.nextStep) {
    return shown(name);
  }
  return shown('No next step', { isMuted: true });
}

/** Which list group a person sits in, checked in the spec's order of precedence. */
export function trackerGroup(
  config: TrackerConfig,
  person: TrackerPerson,
  touches: TrackerTouch[],
  closeAs: TrackerCloseAs | undefined,
  today: string,
): TrackerGroup {
  const channel = channelOf(config, person.channelKey);
  if (channel?.flow === 'steps') {
    if (isLastStage(config, person)) {
      return 'done';
    }
    if (person.nextOn) {
      if (person.nextOn < today) {
        return 'overdue';
      }
      return person.nextOn === today ? 'due_today' : 'coming_up';
    }
    return 'in_progress';
  }
  const role = stageRole(config, person.stageKey);
  const hasPayment = touches.some((t) => t.kind === 'payment');
  if (role === 'won' && closeAs === 'collected' && !hasPayment) {
    return 'won_payment';
  }
  if (role === 'won' || role === 'replied_no' || role === 'lost') {
    return 'done';
  }
  if (person.nextOn && person.nextOn < today) {
    return 'overdue';
  }
  if (person.nextOn === today) {
    return 'due_today';
  }
  if (role === 'decision') {
    return 'decision';
  }
  return 'coming_up';
}

function byName(a: TrackerPerson, b: TrackerPerson) {
  return personName(a).localeCompare(personName(b));
}

/** Sorts one group's people, as each group's header promises. */
export function sortGroup(
  config: TrackerConfig,
  group: TrackerGroup,
  people: TrackerPerson[],
  touchesByPerson: Map<string, TrackerTouch[]>,
) {
  const sorted = [...people];
  if (group === 'overdue' || group === 'coming_up' || group === 'due_today') {
    sorted.sort((a, b) => {
      if (a.nextOn !== b.nextOn) {
        if (!a.nextOn) {
          return 1;
        }
        if (!b.nextOn) {
          return -1;
        }
        return a.nextOn < b.nextOn ? -1 : 1;
      }
      return byName(a, b);
    });
  } else if (group === 'decision') {
    sorted.sort((a, b) => {
      const da = decisionOn(config, touchesByPerson.get(a.id) ?? []) ?? '9999-12-31';
      const db = decisionOn(config, touchesByPerson.get(b.id) ?? []) ?? '9999-12-31';
      return da === db ? byName(a, b) : da < db ? -1 : 1;
    });
  } else if (group === 'in_progress') {
    sorted.sort((a, b) => {
      const ladder = channelOf(config, a.channelKey)?.stages.map((s) => s.key) ?? [];
      const ladderB = channelOf(config, b.channelKey)?.stages.map((s) => s.key) ?? [];
      const ia = a.stageKey ? ladder.indexOf(a.stageKey) : -1;
      const ib = b.stageKey ? ladderB.indexOf(b.stageKey) : -1;
      return ia === ib ? byName(a, b) : ib - ia;
    });
  } else {
    sorted.sort(byName);
  }
  return sorted;
}

export function isOverdueOrToday(person: TrackerPerson, today: string) {
  return !!person.nextOn && person.nextOn <= today;
}

/** A timeline line: "Details sent", "Conversation booked for Thu, Nov 19", "Won · $4,800 booked". */
export function touchText(config: TrackerConfig, touch: TrackerTouch, today: string) {
  if (touch.kind === 'payment') {
    return `${touch.label || 'Payment received'} · ${fmtMoney(touch.amountCents ?? 0)}`;
  }
  if (touch.kind === 'note') {
    return touch.label || 'Note';
  }
  const found = stageByKey(config, touch.stageKey);
  const label = found?.stage.label ?? touch.stageKey ?? '';
  const role = found?.stage.role;
  if (role === 'contacted' && found?.channel.flow === 'sequence') {
    const step = config.sequence.find((s) => s.key === touch.stepKey);
    if (step) {
      return `${step.name} sent`;
    }
    if (touch.label === MESSAGE_LABEL) {
      return 'Message sent';
    }
    return touch.label || label;
  }
  if (role === 'booked' && touch.forOn) {
    return `${label} for ${fmtTrackerDate(touch.forOn, today)}`;
  }
  if (role === 'decision' && touch.forOn) {
    return `${label} · decision on ${fmtTrackerDate(touch.forOn, today)}`;
  }
  if (role === 'won') {
    return `${label} · ${fmtMoney(touch.amountCents ?? 0)} booked`;
  }
  return label;
}

export function touchesByPerson<T extends Pick<TrackerTouch, 'personId'>>(touches: T[]) {
  const map = new Map<string, T[]>();
  for (const touch of touches) {
    const list = map.get(touch.personId);
    if (list) {
      list.push(touch);
    } else {
      map.set(touch.personId, [touch]);
    }
  }
  return map;
}

/**
 * The counts in one line, label first: "Sends 3 · Replies 1 · Conversations
 * held 1". Configured labels are plurals that can't be made singular
 * reliably, so the number follows the label. Steps-only courses list the
 * first channel's stages; a Leads course, "Steps logged 12 · Leads 2", with
 * the spend when the rollup carries it. `withCloses` adds the closes when the
 * participant has a close choice; `brief` keeps the first two items.
 */
export function trackerCountsLine(
  config: TrackerConfig,
  rollup: TrackerRollup,
  opts: { withCloses?: boolean; closeAs?: TrackerCloseAs; brief?: boolean } = {},
) {
  const labels = config.rollupLabels;
  const items: string[] = [];
  if (participantChannels(config)) {
    items.push(`Steps logged ${rollup.steps}`, `Leads ${rollup.leads}`);
    if (rollup.spendCents !== undefined) {
      items.push(`Spend ${fmtMoney(rollup.spendCents)}`);
    }
  } else if (config.channels.some((c) => c.flow === 'sequence')) {
    items.push(
      `${labels.sends} ${rollup.sends}`,
      `${labels.replies} ${rollup.replies}`,
      `${labels.held} ${rollup.held}`,
    );
    if (opts.withCloses && opts.closeAs) {
      items.push(`${labels.closes} ${rollup.closes}`);
    }
  } else {
    for (const stage of config.channels[0]?.stages ?? []) {
      items.push(`${stage.short || stage.label} ${rollup.byStage[stage.key] ?? 0}`);
    }
  }
  return (opts.brief ? items.slice(0, 2) : items).join(' · ');
}
